
export default function RuralUrbanSection() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Rural vs. Urban Land: Understanding the Differences
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            The approach to buying rural acreage versus urban infill lots is completely different. Here's how we evaluate and purchase each type.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div className="bg-gradient-to-br from-green-50 to-white rounded-lg p-8 shadow-lg border-2 border-green-200">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 flex items-center justify-center bg-green-600 rounded-lg">
                <i className="ri-plant-line text-white text-3xl"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900">Rural Land</h3>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="text-xl font-bold mb-3 text-gray-900">Typical Characteristics</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-green-600 mr-2 mt-1"></i>
                    <span>5-100+ acres in size</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-green-600 mr-2 mt-1"></i>
                    <span>Agricultural or unzoned</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-green-600 mr-2 mt-1"></i>
                    <span>No utilities or limited access</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-green-600 mr-2 mt-1"></i>
                    <span>Dirt or gravel roads</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-green-600 mr-2 mt-1"></i>
                    <span>10+ miles from city center</span>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="text-xl font-bold mb-3 text-gray-900">Buyer Profile</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Rural land attracts ranchers, farmers, homesteaders, recreational users, and long-term investors. These buyers want space, privacy, and lower prices per acre. They're comfortable with minimal improvements and off-grid living.
                </p>
              </div>

              <div>
                <h4 className="text-xl font-bold mb-3 text-gray-900">Valuation Factors</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-fill text-green-600 mr-2 mt-1"></i>
                    <span><strong>Price per acre:</strong> $1,000-$5,000 typically</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-fill text-green-600 mr-2 mt-1"></i>
                    <span><strong>Key drivers:</strong> Water rights, soil quality, access</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-fill text-green-600 mr-2 mt-1"></i>
                    <span><strong>Selling time:</strong> 12-24 months traditionally</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-fill text-green-600 mr-2 mt-1"></i>
                    <span><strong>Financing:</strong> Difficult - mostly cash buyers</span>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="text-xl font-bold mb-3 text-gray-900">How We Buy Rural Land</h4>
                <p className="text-gray-700 leading-relaxed">
                  We evaluate rural land based on long-term potential, recreational value, and resale opportunities. We don't need utilities or perfect access. If the property has clear title and reasonable access, we can usually make an offer. We close quickly and handle all paperwork.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-teal-50 to-white rounded-lg p-8 shadow-lg border-2 border-teal-200">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-600 rounded-lg">
                <i className="ri-building-line text-white text-3xl"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900">Urban Infill Lots</h3>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="text-xl font-bold mb-3 text-gray-900">Typical Characteristics</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-1"></i>
                    <span>0.1-1 acre in size</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-1"></i>
                    <span>Residential zoning (R1, R2)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-1"></i>
                    <span>Utilities at the street</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-1"></i>
                    <span>Paved road access</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-1"></i>
                    <span>Within established neighborhoods</span>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="text-xl font-bold mb-3 text-gray-900">Buyer Profile</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Urban lots attract homebuilders, custom home buyers, and developers. These buyers want turnkey building sites with all infrastructure in place. They're willing to pay premium prices for convenience and location.
                </p>
              </div>

              <div>
                <h4 className="text-xl font-bold mb-3 text-gray-900">Valuation Factors</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-fill text-teal-600 mr-2 mt-1"></i>
                    <span><strong>Price per acre:</strong> $20,000-$100,000+ typically</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-fill text-teal-600 mr-2 mt-1"></i>
                    <span><strong>Key drivers:</strong> Location, utilities, neighborhood quality</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-fill text-teal-600 mr-2 mt-1"></i>
                    <span><strong>Selling time:</strong> 6-12 months traditionally</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-fill text-teal-600 mr-2 mt-1"></i>
                    <span><strong>Financing:</strong> More available but still limited</span>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="text-xl font-bold mb-3 text-gray-900">How We Buy Urban Lots</h4>
                <p className="text-gray-700 leading-relaxed">
                  We purchase infill lots for resale to builders or development. We look for buildable lots in good neighborhoods with clear title. Even if your lot has issues (odd shape, small size, easements), we can often make it work. We pay cash and close in 7-14 days.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-8">
          <h3 className="text-3xl font-bold mb-6 text-gray-900 text-center">The Hybrid: Suburban Land</h3>
          <p className="text-lg text-gray-700 mb-6 leading-relaxed max-w-4xl mx-auto">
            Between rural acreage and urban infill lots lies suburban land - typically 1-5 acres on the outskirts of El Paso. These properties combine some benefits of both categories: more space than city lots, but closer to amenities than rural land.
          </p>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="bg-white rounded-lg p-6 shadow-lg">
              <h4 className="font-bold text-gray-900 mb-3">Typical Price Range</h4>
              <p className="text-gray-700 leading-relaxed">
                $5,000-$15,000 per acre depending on utilities and location. More than rural, less than urban.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-lg">
              <h4 className="font-bold text-gray-900 mb-3">Common Buyers</h4>
              <p className="text-gray-700 leading-relaxed">
                Families wanting space for horses, gardens, or workshops. Small-scale developers. Homesteaders.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-lg">
              <h4 className="font-bold text-gray-900 mb-3">Our Approach</h4>
              <p className="text-gray-700 leading-relaxed">
                We evaluate suburban land based on both residential potential and long-term appreciation as the city expands.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
